import { Subscription } from "rxjs";
import { EnvironmentDetailsService } from "./../../services/environment-details.service";
import { Component, OnInit } from "@angular/core";
import { faCloud } from "@fortawesome/free-solid-svg-icons";
import { PeeringTab } from "../../enums/peering-tabs.enum";
import { ActivatedRoute, Router } from "@angular/router";
import { IEnvironmentDetails } from "../../interfaces/environment-details";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { IAddPeerForm } from "../../interfaces/add-peer-form";
import {
  NgbModal,
  NgbModalConfig,
  NgbModalRef,
} from "@ng-bootstrap/ng-bootstrap";
import { PopupComponent } from "../popup/popup.component";
import { CloudNotificationService } from "src/app/services/cloud-notification.service";
import { PopupAction } from "../../enums/popup-action.enum";
import { CloudNotificationStyle } from "src/app/models/cloud-notification-style.model";
import { environment } from "src/environments/environment";
import Helper from "src/app/Util/helper";
import { IURLParams } from "../../interfaces/url-param.interface";

@Component({
  selector: "app-vnet-peering",
  templateUrl: "./vnet-peering.component.html",
  styleUrls: ["./vnet-peering.component.sass"],
})

export class VnetPeeringComponent implements OnInit {

  iconList: { [key: string]: any };
  wizardTabList: string[];
  pageTitle: string;
  currentTabIndex: number;
  PeeringTab = PeeringTab;
  modalRef: NgbModalRef;
  isLoading: boolean;

  peeringForm: FormGroup;
  environments: IEnvironmentDetails[] = [];
  currentEnvironment: IEnvironmentDetails;
  selectedEnvironments: IEnvironmentDetails[] = [];
  envDetailsSubscription: Subscription;

  private subscriptionId: string;
  private envName: string;

  constructor(
    private readonly currentRoute: ActivatedRoute,
    private router: Router,
    private envDetailsService: EnvironmentDetailsService,
    private fb: FormBuilder,
    private modal: NgbModal,
    private modalConfig: NgbModalConfig,
    private notificationService: CloudNotificationService
  ) { }

  ngOnInit(): void {
    this.initParams();
    this.initializeIcons();
    this.setPageTitle();
    this.setWizardTabs();
    this.onWizardTabChange(0);
    this.initializeForm();
    this.getAllEnvironments();
    this.getCurrentEnvironmentInfo();
    this.setupModalConfig();
    this.isLoading = false;    

  }

  setPageTitle(): void {
    this.pageTitle = "Create peering for WAN Replication";
  }

  initializeIcons(): void {
    this.iconList = { faCloud };
  }

  setWizardTabs(): void {
    this.wizardTabList = [PeeringTab.Configuration, PeeringTab.Review];
  }

  updatePageTitle(title: string): void {
    this.pageTitle = title;
  }

  onWizardTabChange(index: number): void {
    this.currentTabIndex = index;
  }

  initializeForm(): void {
    this.peeringForm = this.fb.group({
      envName: ["", Validators.required],
    });
  }

  setupModalConfig() {
    this.modalConfig.backdrop = "static";
    this.modalConfig.keyboard = false;
    this.modalConfig.centered = true;
  }

  filterNotSelectedEnvironments(
    response: IEnvironmentDetails[]
  ): IEnvironmentDetails[] {
    return response.filter(
      (env) =>
        !this.selectedEnvironments.some(
          (selectedEnv) => selectedEnv.name === env.name
        )
    );
  }

  private initParams(): void {
    const urlParams = this.getParamsFromUrl();
    this.subscriptionId = urlParams.subscriptionId;
    this.envName = urlParams.environmentId;
  }

  private getParamsFromUrl(): IURLParams {
    let params: IURLParams;
    this.currentRoute.params.subscribe((param) => (params = param));
    return {
      subscriptionId: params?.cloudSubscriptionId ?? "",
      environmentId: params?.environmentId ?? "",
    };
  }

  getAllEnvironments() {
    this.envDetailsSubscription = this.envDetailsService
      .getEnvironmentsDetails(this.subscriptionId)
      .subscribe(
        (response: IEnvironmentDetails[]) => {
         this.environments = this.filterNotSelectedEnvironments(response);          
        },

        (error) => {
          this.notificationService.createorUpdateToastNotification(
            "get-envs-details",
            "Error: " + Helper.resolveErrorMessage(error),
            CloudNotificationStyle.danger
          );
        }
      );
  }

  getCurrentEnvironmentInfo() {
    this.envDetailsSubscription = this.envDetailsService
      .getEnvironmentInfo(this.envName)
      .subscribe(
        (response: IEnvironmentDetails) => {
          this.currentEnvironment = response;
        },
        (error) => {
          this.notificationService.createorUpdateToastNotification(
            "get-env-info",
            "Error: " + Helper.resolveErrorMessage(error),
            CloudNotificationStyle.danger
          );
        }
      );
  }

  redirectToEnvs() {
    this.router.navigate([
      Helper.formatString(
        environment.routes.environment,
        this.subscriptionId,
        this.envName
      ),
    ]);
  }

  createEnvironmentPeering() {
    this.modalRef.componentInstance.isLoading = true;
    const currentEnvName = this.currentEnvironment.name;
    const selectedEnvNames = this.selectedEnvironments.map((env) => env.name);

    this.envDetailsService
      .createEnvPeering(currentEnvName, selectedEnvNames)
      .subscribe((response: any) => {
        if (response) {
          this.modalRef.componentInstance.isLoading = false;
          this.modalRef.dismiss();
          this.redirectToEnvs();
        } else {
          this.modalRef.componentInstance.isLoading = false;
          this.modalRef.close();
          this.notificationService.createorUpdateToastNotification(
            "create-env-peering",
            "Error: " + Helper.resolveErrorMessage(response.error),
            CloudNotificationStyle.danger
          );
        }
      });
  }

  handleEnvironmentSelected(environmentKey: string): void {
    this.peeringForm.get("envName").setValue(environmentKey);
  }

  isEnvironmentNew(envName: string): boolean {
    return !this.selectedEnvironments.some((env) => env.name === envName);
  }

  addEnvironmentToStorage(envName: string) {
    const env: IEnvironmentDetails | undefined = this.environments.find(
      (env) => env.name === envName
    );
    if (env) {
      this.selectedEnvironments.push(env);
      this.environments = this.environments.filter(
        (env) => env.name !== envName
      );
      this.peeringForm.get("envName").setValue("");
    }
  }

  handlePeerSubmission(addPeerFormData: IAddPeerForm) {
    const envName = addPeerFormData.envName;

    if (this.isEnvironmentNew(envName)) {
      this.addEnvironmentToStorage(envName);
      this.notificationService.createorUpdateToastNotification(
        "env-added",
        "Environment is added Successfully",
        CloudNotificationStyle.success
      );
    } else {
      this.notificationService.createorUpdateToastNotification(
        "env-already-added",
        "Environment is Already Added",
        CloudNotificationStyle.danger
      );
    }
  }

  deleteEnvironment(environment: IEnvironmentDetails) {
    const envName = environment.name;
    this.selectedEnvironments = this.selectedEnvironments.filter(
      (env) => env.name !== envName
    );
    this.getAllEnvironments();
    this.peeringForm.get("envName").setValue("");
    this.notificationService.createorUpdateToastNotification(
      "delete-env",
      "Environment deleted successfully",
      CloudNotificationStyle.success
    );
  }

  openConfirmDeletionPopUp(environment: IEnvironmentDetails) {
    this.modalRef = this.modal.open(PopupComponent);
    this.modalRef.componentInstance.actionType = PopupAction.Delete;

    this.modalRef.componentInstance.confirmDeletion.subscribe(
      (deletionConfirmed) => {
        if (deletionConfirmed) {
          this.modalRef.close();
          this.deleteEnvironment(environment);
        }
      }
    );
  }

  openConfirmCancelPeering() {
    this.modalRef = this.modal.open(PopupComponent);
    this.modalRef.componentInstance.actionType = PopupAction.Cancel;

    this.modalRef.componentInstance.confirmCancellation.subscribe(
      (deletionConfirmed) => {
        if (deletionConfirmed) {
          this.modalRef.close();
          this.redirectToEnvs();
        }
      }
    );
  }

  openConfirmPeeringCreation() {
    this.modalRef = this.modal.open(PopupComponent);
    this.modalRef.componentInstance.actionType = PopupAction.CreatePeering;
    this.modalRef.componentInstance.isLoading = this.isLoading;

    this.modalRef.componentInstance.confirmPeeringCreation.subscribe(
      (creationConfirmed) => {
        if (creationConfirmed) {
          this.createEnvironmentPeering();
        }
      }
    );
  }

  confirmDeleteEnvironment(environment: IEnvironmentDetails) {
    this.openConfirmDeletionPopUp(environment);
  }

  confirmCancelPeering() {
    this.openConfirmCancelPeering();
  }

  confirmPeeringCreation() {
    this.openConfirmPeeringCreation();
  }
}
